/**
 * Header search engine — a lightweight, client-only Coveo engine for the global
 * header search box (rendered on every page, not just /search).
 *
 * Built on `defineStandaloneSearchBox`, Coveo's primitive for a search box that
 * lives outside the results page: it serves ML query suggestions and, on submit,
 * hands back a redirect URL instead of running the query. The results engine on
 * /search then executes it, seeded from the URL server-side.
 *
 * Deliberately a SEPARATE definition (and therefore a separate React context)
 * from the results engine in engineDefinition.ts: the header must work on pages
 * that have no results engine at all, and coupling the two is what made the CSR
 * header poll a module singleton and render a disabled input until it appeared.
 *
 * It executes no result searches, so no page that renders the header gains a
 * Coveo query, and nothing here forces dynamic rendering — the engine is
 * bootstrapped entirely in the browser (see ../HeaderSearch/HeaderSearch.tsx).
 *
 * IMPORT RULE: as everywhere under ssr/, the SSR/define API comes only from
 * `@coveo/headless-react/ssr`; a bare `@coveo/headless` resolves to
 * atomic-react's v2.39 (no `/ssr`).
 */
import {
  defineContext,
  defineRecentQueriesList,
  defineSearchEngine,
  defineStandaloneSearchBox,
  type NavigatorContext,
} from '@coveo/headless-react/ssr';
import { CoveoSearchHub } from '@lib/coveo/coveoConstants';
import { fetchSearchToken } from '@lib/coveo/searchTokenHelpers';
import * as Sentry from '@sentry/nextjs';
import { DEFAULT_LOCALE } from 'utils';
import { CLIENT_ID_STORAGE_KEY, hasFunctionalConsent } from './clientIdStorage';

/** Where the standalone box sends the shopper on submit (SSR-friendly query). */
export const SEARCH_REDIRECTION_URL = '/search';

/** The most suggestions the dropdown lists for a typed query. */
export const SUGGESTIONS_SHOWN = 5;

/**
 * Asked of Coveo ML. It exceeds what is shown so the QS trending arm's
 * client-side filter (denylist, recents) can fall back on later suggestions
 * and still fill its Top list.
 */
const SUGGESTIONS_REQUESTED = 10;

/** Recent queries kept alongside the suggestions. */
const MAX_RECENT_QUERIES = 5;

/**
 * Bold the part of a suggestion the shopper has NOT typed yet, the convention
 * the legacy Atomic search box used (it wrapped `notMatchDelimiters` in a bold
 * span). Without delimiters Coveo returns `highlightedValue` as plain text and
 * the dropdown renders one undifferentiated string.
 */
const HIGHLIGHT_OPTIONS = {
  notMatchDelimiters: { open: '<b>', close: '</b>' },
};

/**
 * Report a renewal that produced no usable token. A rejected response and a 2xx
 * body without a token are the same broken endpoint from the box's side, and
 * only the first one arrives as an exception; without this the second is a
 * silent stream of unauthorized query-suggest calls.
 */
function captureFailedRenewal(error: unknown): string {
  Sentry.withScope((scope) => {
    scope.setTags({
      'coveo.hub': CoveoSearchHub.SEARCH,
      'coveo.step': 'token_renew',
    });
    Sentry.captureException(error);
  });
  return '';
}

/**
 * Per-mount engine definition. Each `build()` registers an engine-retaining
 * callback on the definition's token manager that is never released, so a
 * definition that gets built must not be shared: every engine a shared one ever
 * built would be held for the life of the SPA session. Header renders a single
 * SearchWrapper, placed by CSS order rather than once per layout, so a page load
 * builds one engine — but Strict Mode replays the bootstrap effect on every
 * mount in development, and accumulation is silent wherever it does happen.
 *
 * `locale` drives Coveo's linguistic processing for query suggestions, matching
 * what the results engine passes for searches; the caller reads it from the URL.
 */
export function createHeaderSearchEngineDefinition(
  locale: string = DEFAULT_LOCALE
) {
  return defineSearchEngine({
    configuration: {
      organizationId: process.env.NEXT_PUBLIC_COVEO_ORGANIZATION_ID!,
      accessToken: 'pending-token-set-client-side',
      // Renew on 401 with a fresh ANONYMOUS token: the header box only queries
      // public query-suggest data, and a per-user token would tie the header to
      // the session. Must resolve to a string — a rejection would propagate
      // into the SDK's token manager and hide a broken token endpoint.
      renewAccessToken: () =>
        fetchSearchToken(CoveoSearchHub.SEARCH, true).then(
          (r) =>
            r?.token ??
            captureFailedRenewal(
              new Error('Header search token renewal carried no token')
            ),
          captureFailedRenewal
        ),
      search: { searchHub: CoveoSearchHub.SEARCH, locale },
      // Legacy UA protocol, like the results engine. Tracking stays off: this
      // engine executes no searches, and the search the shopper lands on is
      // logged by the results engine (AnalyticsConsentSync), not the header
      // box.
      analytics: { analyticsMode: 'legacy', enabled: false },
    },
    controllers: {
      standaloneSearchBox: defineStandaloneSearchBox({
        options: {
          redirectionUrl: SEARCH_REDIRECTION_URL,
          numberOfSuggestions: SUGGESTIONS_REQUESTED,
          highlightOptions: HIGHLIGHT_OPTIONS,
        },
      }),
      // Recent queries are engine state fed by executed searches. This engine
      // executes none and the header full-navigates away on submit, so the list
      // is empty today; the dropdown renders it when present so the surface is
      // ready for a persistence decision (out of scope here).
      recentQueries: defineRecentQueriesList({
        options: { maxLength: MAX_RECENT_QUERIES },
      }),
      // Carries the pipeline-selecting context values set at build time. The
      // query-suggest ML model sits behind a pipeline Condition on them, so
      // without this controller the box asks Coveo for suggestions on every
      // keystroke and is answered with an empty completion list every time
      // (see ./headerSearchContext).
      context: defineContext(),
    },
  });
}

/**
 * Client-side singleton: the generated controller hooks and the StateProvider
 * must come from one shared definition (they only read React context), but
 * nothing is ever built on this one — see the factory above.
 */
export const headerSearchEngineDefinition =
  createHeaderSearchEngineDefinition();

export const {
  controllers: headerControllers,
  StateProvider: HeaderStateProvider,
} = headerSearchEngineDefinition;

export type HeaderSearchEngine = Awaited<
  ReturnType<typeof headerSearchEngineDefinition.build>
>;

/**
 * Coveo receives a client id either way — it ties one visit's query-suggest
 * calls together. Only its persistence is gated, on the functional category the
 * results engine's analytics run behind (AnalyticsConsentSync), because
 * that is what turns the id into a cross-session identifier on the device.
 * Without consent it lives for the page load, like the SSR results engine's.
 */
function getBrowserClientId(): string {
  try {
    if (!hasFunctionalConsent()) {
      // Withdrawn consent has to take the earlier id with it, or the shopper
      // stays identified by what a previous visit stored.
      window.localStorage.removeItem(CLIENT_ID_STORAGE_KEY);
      return crypto.randomUUID();
    }
    const existing = window.localStorage.getItem(CLIENT_ID_STORAGE_KEY);
    if (existing) {
      return existing;
    }
    const generated = crypto.randomUUID();
    window.localStorage.setItem(CLIENT_ID_STORAGE_KEY, generated);
    return generated;
  } catch {
    // Storage denied (Safari private mode, blocked cookies): a per-load id
    // still gives Coveo a coherent visit, it just doesn't survive navigation.
    return crypto.randomUUID();
  }
}

/**
 * Navigator context for the header engine, sourced from browser globals: this
 * engine bootstraps in the browser, so there is no server component to marshal
 * a context from the request headers (NextJsAppRouterNavigatorContext).
 * `forwardedFor` is SSR-only and omitted. Register it before building, or Coveo
 * warns about a missing navigator context and the box's query-suggest calls go
 * out without referrer/location/clientId.
 */
export function browserNavigatorContext(): NavigatorContext {
  return {
    clientId: getBrowserClientId(),
    location: window.location.href,
    referrer: document.referrer || null,
    userAgent: window.navigator.userAgent,
  };
}
